import _ from 'lodash';
import React, {useEffect} from 'react';
import styles from './Score.module.scss';
import posed from 'react-pose';
import {allPoses, teamScorePoses} from '../gameHelpers';
import {keysToSingleValue} from '../utils/objectFactory';
import makeNimbus from './Nimbus';
import * as audioBundle from '../audioBundle';

const delay = 2000;
const delayOtherTries = 3000;
const duration = 1000;

const normalScorePoses = ['initial', 'result'];

const Container = posed.div({
    ...keysToSingleValue(normalScorePoses, {
        y: 0,
        opacity: 1,
        transition: {
            ease: 'easeIn',
            duration,
        },
    }),
    ...keysToSingleValue(teamScorePoses, {
        y: props => (props.active ? '-2em' : 0),
        opacity: props => (props.active ? 1 : 0.3),
        transition: props => ({
            ease: 'easeOut',
            delay: props.tryIndex === 0 ? delay : delayOtherTries,
            duration,
        }),
    }),
    ...keysToSingleValue(_.difference(allPoses, normalScorePoses, teamScorePoses), {
        y: 0,
        opacity: 0.3,
        transition: {
            ease: 'easeIn',
            delay,
            duration,
        },
    }),
    result: {
        opacity: 1,
        transition: {
            type: 'keyframes',
            times: [0, 0.9, 1],
            values: [0, 0, 1],
            duration: 500,
        },
    },
});

function getTeamDelay(props) {
    return props.tryIndex === 0 ? delay : delayOtherTries;
}

const Nimbus = makeNimbus({
    pose: 'wait-try',
    delay: props => (props.tryIndex === 0 ? delay : delayOtherTries),
    duration,
});

const WinScoreNimbus = makeNimbus({
    pose: ['right-answer', 'right-answer-multiple'],
    delay: 200,
    duration,
});

const WinNimbus = makeNimbus({
    pose: 'result',
    delay: 2000,
    duration: 1000,
});

export default function ScoreComponent({
    index,
    stage,
    teamDirection,
    score,
    active,
    tryIndex,
    win,
}) {
    useTeamSound({tryIndex, stage});

    const teams = ['A', 'B'];
    const teamName = teams[index];

    const scoreStyle = {
        alignSelf: index === 0 ? 'flex-start' : 'flex-end',
    };

    const nimbusStyle = {
        left: teamDirection > 0 ? '-100%' : 'auto',
        right: teamDirection < 0 ? '-100%' : 'auto',
    };

    const winNimbusStyle = {
        left: teamDirection > 0 ? '-70%' : 'auto',
        right: teamDirection < 0 ? '-70%' : 'auto',
    };

    return (
        <Container active={active} tryIndex={tryIndex} className={styles.scorePanel}>
            <header className={styles.scoreHeader}>Team {teamName}</header>
            <div className={styles.score} style={scoreStyle}>
                {score} pts
            </div>

            {active && renderScoreNimbus(Nimbus, {teamDirection, tryIndex})}
            {active && renderScoreNimbus(WinScoreNimbus, {teamDirection, tryIndex})}

            {active && (
                <Nimbus
                    className={styles.nimbus}
                    style={nimbusStyle}
                    count={5}
                    angle1={-90 - teamDirection * 10}
                    angle2={-90 - teamDirection * 85}
                    direction={-teamDirection}
                    radius1={0.8}
                    radius2={1}
                    tryIndex={tryIndex}
                />
            )}

            {win && (
                <WinNimbus
                    className={styles.winNimbus}
                    style={winNimbusStyle}
                    count={5}
                    angle1={90 + teamDirection * 120}
                    angle2={90 - teamDirection * 40}
                    direction={-teamDirection}
                    radius1={0.7}
                    radius2={1}
                    strokeWidth={55}
                    strokeLinecap='round'
                />
            )}
        </Container>
    );
}

function useTeamSound({stage, tryIndex}) {
    useEffect(() => {
        const timer = setTimeout(() => {
            if (stage === 'wait-try') {
                audioBundle.play('team');
            }
        }, getTeamDelay({tryIndex}));
        return () => clearTimeout(timer);
    }, [stage]);
}

function renderScoreNimbus(NimbusComponent, {teamDirection, tryIndex}) {
    const nimbusStyle = {
        left: teamDirection > 0 ? '-100%' : 'auto',
        right: teamDirection < 0 ? '-100%' : 'auto',
    };

    return (
        <NimbusComponent
            className={styles.nimbus}
            style={nimbusStyle}
            count={5}
            angle1={-90 - teamDirection * 10}
            angle2={-90 - teamDirection * 85}
            direction={-teamDirection}
            radius1={0.8}
            radius2={1}
            tryIndex={tryIndex}
        />
    );
}
